import { Link } from "@tanstack/react-router";
import type { LeaderboardPlayer } from "@/api/ranking";
import type { ColumnDef } from "@tanstack/react-table";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { getPointsBadge } from "@/lib/utils";

const periodCell = (value: number | null) => {
    if (!value) return <span className="text-sm text-on-surface-variant">-</span>;
    const { bg, text, border } = getPointsBadge(value);
    return (
        <div className={`inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-sm border ${border}`}>
            {value}
        </div>
    );
};

export const leaderboardTableColumns: ColumnDef<LeaderboardPlayer>[] = [
    {
        id: "rank",
        header: "Rank",
        size: 40,
        cell: ({ row, table }) => {
            const allData = table.getCoreRowModel().rows;
            const sortedData = [...allData].sort(
                (a, b) => (b.original.totalPoints ?? 0) - (a.original.totalPoints ?? 0)
            );
            const rank = sortedData.findIndex((r) => r.id === row.id) + 1;
            const label = `#${rank.toString().padStart(2, "0")}`;
            return (
                <span
                    className={`font-headline font-bold text-sm ${
                        rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                    }`}
                >
                    {label}
                </span>
            );
        },
    },
    {
        id: "player",
        header: "Player",
        size: 110,
        cell: ({ row }) => (
            <Link
                to="/player/$playerId"
                params={{ playerId: row.original.playerId.toString() }}
                className="font-bold text-on-surface hover:text-primary hover:underline transition-colors block overflow-hidden text-ellipsis whitespace-nowrap w-full"
            >
                {row.original.playerName} {row.original.playerLastName}
            </Link>
        ),
    },
    {
        accessorKey: "role",
        header: "Role",
        size: 52,
        cell: ({ getValue }) => {
            const roles = getValue() as string[];
            const display = roles.length > 0 ? roles[0] : "N/A";
            return (
                <span className="px-2 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant">
                    {display}
                </span>
            );
        },
    },
    {
        accessorKey: "period1",
        header: "P1",
        size: 56,
        cell: ({ getValue }) => periodCell(getValue() as number | null),
    },
    {
        accessorKey: "period2",
        header: "P2",
        size: 56,
        cell: ({ getValue }) => periodCell(getValue() as number | null),
    },
    {
        accessorKey: "period3",
        header: "P3",
        size: 56,
        cell: ({ getValue }) => periodCell(getValue() as number | null),
    },
    {
        accessorKey: "totalPoints",
        header: "Total Points",
        size: 56,
        cell: ({ getValue }) => {
            const value = getValue() as number | null;
            if (!value) {
                return (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="cursor-help text-on-surface-variant">-</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                Player has spent insufficient time on the ice to be objectively evaluated against our
                                KPI scale.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                );
            }
            const { bg, text, border } = getPointsBadge(value);
            return (
                <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                >
                    {value}
                </div>
            );
        },
    },
];
